import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import IconDelete from '@assets/images/icons/trashcan.svg'
import IconDownload from '@assets/images/icons/download.svg'
import IconCheckMedium from '@assets/images/icons/check_medium.svg'
import DocumentDetailActionButtons from '@/modules/document/components/edit/document-detail-action-buttons'

import ThemeInterface from '@/theme-interface'
import { ButtonIconSquare, CancelButton, SquareIconButton, SquareIconLink } from '@/modules/base/components/buttons'
import ExpenseAndRevenueDrawerActionButtons from '@/modules/expense-revenue/components/drawer/drawer-action-buttons'
import DocumentShareMenu from '@/modules/document/components/edit/document-share-menu'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    padding: '21px 25px',
    display: 'flex',
    '& button, a': {
      height: '40px',
      maxHeight: '40px',
    },
    '& $buttonGroup:first-child': {
      marginLeft: 'unset',
    },
  },
  buttonGroup: {
    display: 'flex',
    marginLeft: 'auto',
    '& > *:not(:first-child)': {
      marginLeft: '15px',
    },
  },
  additionalButtons: {
    '& button': {
      backgroundColor: 'white',
      '& svg': {
        fill: theme.black,
      },
    },
  },
  iconButton: {
    width: '40px',
    maxWidth: '40px',
  },
  abortButton: {
    padding: '0 40px',
    overflow: 'none',
  },
  textButton: {
    overflow: 'none',
  },
}))

interface ReceiptPreviewFileListPanelFooterInterface {
  file?: any
  abortLabel: string
  selectLabel: string
  removeLabel: string
  downloadLabel: string
  downloadUrl: string
  hideSelectButton?: boolean
  hideCancelButton?: boolean
  hideRemoveButton?: boolean
  hideDownloadButton?: boolean
  showShareMenu?: boolean
  showScanButton?: boolean
  showContextMenu?: boolean
  isDetailView?: boolean
  isDisabled?: boolean
  isEconomicAsset?: boolean
  detailActionsVariant?: string
  className?: string
  children?: ReactNode
  expense?: any
  revenue?: any
  onAbort?: () => void
  onRemove?: () => void
  onSelect?: (selected: boolean) => void
  onScan?: () => void
}

const ReceiptPreviewFileListPanelFooter = (props: ReceiptPreviewFileListPanelFooterInterface) => {
  const {
    abortLabel,
    removeLabel,
    downloadLabel,
    downloadUrl,
    selectLabel,
    className,
    onRemove,
    onAbort,
    onSelect,
    hideSelectButton,
    hideCancelButton,
    hideDownloadButton,
    hideRemoveButton,
    showShareMenu = false,
    file,
    expense,
    showContextMenu,
    revenue,
    isDisabled,
    isDetailView,
    detailActionsVariant = 'primary',
    onScan,
    children,
    isEconomicAsset,
  } = props || {}
  const classes = useStyles()

  if (isDetailView) {
    return (
      <ExpenseAndRevenueDrawerActionButtons
        file={file}
        expense={expense}
        revenue={revenue}
        isDisabled={isDisabled}
        onClose={onAbort}
        isEconomicAsset={isEconomicAsset} />
    )
  }

  return (
    <div className={clsx(classes.root, className)}>
      <div className={clsx(classes.buttonGroup, classes.additionalButtons)}>
        {showContextMenu && <DocumentDetailActionButtons
          file={file}
          variant={detailActionsVariant}
          expense={expense}
          revenue={revenue}
          isDisabled={isDisabled}
          onRemove={onRemove}
          onScan={onScan}
        />}
        {showShareMenu && (
          <DocumentShareMenu file={file} onOpenEmailDrawer={onSelect} />
        )}
        {!hideRemoveButton && (
          <SquareIconButton
            customClass={classes.iconButton}
            disabled={onRemove == null}
            onClick={onRemove}
            icon={IconDelete}
            tooltip={removeLabel} />
        )}
        {!hideDownloadButton && (
          <SquareIconLink
            customClass={classes.iconButton}
            icon={IconDownload}
            tooltip={downloadLabel}
            href={downloadUrl}
            target='_blank' />
        )}
      </div>
      <div className={classes.buttonGroup}>
        {children}
        {!hideCancelButton && (
          <CancelButton customClass={classes.abortButton} onClick={onAbort} label={abortLabel} />
        )}
        {!hideSelectButton && (
          <ButtonIconSquare
            icon={IconCheckMedium}
            customClass={classes.textButton}
            onClick={() => onSelect(true)}
            label={selectLabel}
            disabled={isDisabled} />
        )}
      </div>
    </div>
  )
}

export default ReceiptPreviewFileListPanelFooter
